import type { ListingApplicationPayload } from '#/api/ats';
import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type { ApiRequestedAttachment } from '#/api/types/ApiRequestedAttachment';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { InfoBox } from '#/components/InfoBox/InfoBox';
import { BodySmall } from '#/components/Text/BodySmall';
import { useIsomorphicEnvironment } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { RenderListingApplicationResumeField } from '#/modules/listing/components/ApplicationFields/ListingAtsApplicationFields';
import { ListingAtsApplicationFields } from '#/modules/listing/components/ApplicationFields/ListingAtsApplicationFields';
import type { RenderRequestedAttachmentFileFieldFn } from '#/modules/listing/components/ApplicationFields/ListingRequestedAttachmentsFieldSwitch';
import { gutterWidth } from '#/theme/theme';

import { ListingViewAtsApplicationFormConsentField } from './ListingViewAtsApplicationFormConsentField';

type Props = {
  listingId: string;
  listingType: ApiListingApplicationListingType;
  requestedAttachments: ApiRequestedAttachment[];
  /**
   * This form is used both for capturing applications and for admins as preview.
   *
   * To make explicit that some properties only exist in "application mode",
   * they are grouped under the `submission` prop
   */
  submission: {
    listingApplicationInitialValues: Partial<ListingApplicationPayload>;
    listingApplicationInitialErrors: APIErrors;
    onSubmit: FormSubmitFunction<ListingApplicationPayload>;
    requestedAttachmentsHash: string;
  } | null;
  submitButtonText: string;
  renderRequestedAttachmentFileField: RenderRequestedAttachmentFileFieldFn;
  renderResumeField: RenderListingApplicationResumeField;
};

export function ListingViewAtsApplicationForm({
  listingId,
  listingType,
  requestedAttachments,
  submission,
  submitButtonText,
  renderRequestedAttachmentFileField,
  renderResumeField,
}: Props) {
  const { i18n } = useI18n();
  const { isomorphicEnvironment } = useIsomorphicEnvironment();

  const allFieldsRequired =
    requestedAttachments.filter((ra) => !ra.isRequired).length === 0;

  return (
    <Form
      analyticsTitle="[main] Org listing page - how to apply section - apply"
      usageContext="page"
      qaId="listing-application-form"
      enableNavigationWarning
      errors={submission?.listingApplicationInitialErrors}
      initialValues={{
        requestedAttachmentsHash: submission?.requestedAttachmentsHash,
        id: listingId,
        requestedAttachments: Array(requestedAttachments.length).fill(null),
        type: listingType,
        replaceCurrentResume: false,
        ...submission?.listingApplicationInitialValues,
      }}
      onSubmit={(data) => submission?.onSubmit(data)}
    >
      <>
        {allFieldsRequired ? (
          <div style={{ marginTop: -12, marginBottom: 16 }}>
            <InfoBox $type="warning" $small>
              <BodySmall>{i18n('All fields are required')}</BodySmall>
            </InfoBox>
          </div>
        ) : null}

        <GeneralError />

        <div style={{ marginTop: 24 }}>
          <ListingAtsApplicationFields
            listingType={listingType}
            allFieldsRequired={allFieldsRequired}
            disabled={!submission}
            requestedAttachments={requestedAttachments}
            initialResumeFileId={
              submission?.listingApplicationInitialValues.resumeS3FileId ?? null
            }
            renderRequestedAttachmentFileField={
              renderRequestedAttachmentFileField
            }
            renderResumeField={renderResumeField}
          />
        </div>

        <ListingViewAtsApplicationFormConsentField
          isEU={isomorphicEnvironment.isEU}
          disabled={!submission}
        />

        <div
          style={{
            marginTop: gutterWidth,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <SubmitButton size="large" disabled={!submission}>
            {submitButtonText}
          </SubmitButton>
        </div>
      </>
    </Form>
  );
}
